/**
 * Centralized pricing / plan configuration.
 *
 * This is the ONE place that says what plans exist, what they're called,
 * what they list for, and which Worker env var holds each plan's Stripe
 * Price ID. Everything else reads from here:
 *
 *   - apps/api/src/lib/stripe/config.ts   resolves `stripePriceEnvVar` to a
 *                                         real Stripe Price ID at runtime
 *   - apps/api/src/routes/billing.ts      only lets callers buy `selfServe`
 *                                         plans, by plan KEY (never by a
 *                                         client-supplied price id)
 *   - apps/api/src/routes/admin.ts        estimated-MRR list prices
 *   - apps/web/src/lib/pricing-config.ts  the landing page's prices
 *   - apps/web/src/routes/customer/Billing.tsx
 *
 * To change a price: edit `monthlyPriceCents` here AND create a matching
 * Price in the Stripe (test-mode) dashboard, then point the plan's env var
 * at the new `price_...` id. The dollar amount that actually gets charged
 * is whatever the Stripe Price says -- `monthlyPriceCents` is the list
 * price used for display and the admin MRR *estimate*, so keep them in
 * sync. To add a plan: add its key to `SubscriptionPlan`
 * (types/finance.ts), then add one entry below.
 *
 * Stripe Price IDs deliberately do NOT live in this file: they differ
 * between test and live mode (and between environments), so they're
 * configuration, not code.
 */
import type { SubscriptionPlan } from "../types/finance";

export interface BillingPlanConfig {
  plan: SubscriptionPlan;
  /** Customer-facing name. */
  name: string;
  /** List price in integer cents per month. 0 for plans that aren't sold
   * at a fixed price (trial) or have no list price (enterprise). */
  monthlyPriceCents: number;
  /** True if a customer can start Stripe Checkout for this plan
   * themselves. Trial and enterprise ("talk to us") are not self-serve. */
  selfServe: boolean;
  /** Name of the Worker env var that holds this plan's Stripe Price ID
   * (`price_...`). Null for plans that aren't purchasable via Checkout. */
  stripePriceEnvVar: "STRIPE_PRICE_STARTER" | "STRIPE_PRICE_PRO" | null;
}

export const BILLING_PLANS: Record<SubscriptionPlan, BillingPlanConfig> = {
  trial: {
    plan: "trial",
    name: "Trial",
    monthlyPriceCents: 0,
    selfServe: false,
    stripePriceEnvVar: null,
  },
  starter: {
    plan: "starter",
    name: "Starter",
    monthlyPriceCents: 7900,
    selfServe: true,
    stripePriceEnvVar: "STRIPE_PRICE_STARTER",
  },
  pro: {
    plan: "pro",
    // The stored key is `pro`; the landing page has always called this
    // tier "Growth", so that's the customer-facing name everywhere.
    name: "Growth",
    monthlyPriceCents: 19900,
    selfServe: true,
    stripePriceEnvVar: "STRIPE_PRICE_PRO",
  },
  enterprise: {
    plan: "enterprise",
    name: "Enterprise",
    // "Custom" pricing in practice -- no list price exists to estimate
    // from, so it contributes 0 to the MRR estimate rather than a guess.
    monthlyPriceCents: 0,
    selfServe: false,
    stripePriceEnvVar: null,
  },
};

/** Plans a customer can buy through Stripe Checkout, in display order. */
export type SelfServePlan = "starter" | "pro";
export const SELF_SERVE_PLANS: SelfServePlan[] = ["starter", "pro"];

export function isSelfServePlan(value: unknown): value is SelfServePlan {
  return typeof value === "string" && (SELF_SERVE_PLANS as string[]).includes(value);
}

/** Free days added at the start of a *new Stripe subscription* created by
 * Checkout. 0 = the card is charged immediately. (The app's own signup
 * "trial" is separate and involves no Stripe subscription at all.) */
export const CHECKOUT_TRIAL_DAYS = 0;

export function planDisplayName(plan: SubscriptionPlan): string {
  return BILLING_PLANS[plan]?.name ?? plan;
}

/**
 * Plan list prices, for reporting only: used to compute an *estimated* MRR
 * on the platform-admin finance/overview dashboards from real subscription
 * counts. Derived from `BILLING_PLANS` so there is one price table. It is
 * not what Stripe actually billed (coupons, proration and taxes all make
 * real revenue differ), so every place that uses it must label the result
 * "estimated". Actual collected revenue is `platform_revenue`, written from
 * Stripe `invoice.paid` webhooks.
 */
export const PLAN_MONTHLY_PRICE_CENTS: Record<SubscriptionPlan, number> = {
  trial: BILLING_PLANS.trial.monthlyPriceCents,
  starter: BILLING_PLANS.starter.monthlyPriceCents,
  pro: BILLING_PLANS.pro.monthlyPriceCents,
  enterprise: BILLING_PLANS.enterprise.monthlyPriceCents,
};
